import React from 'react';
import { useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';

import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanBreakdown } from 'selectors/getLoanBreakdown';
import ActionCard, { ActionCardVariant, ActionCardSize } from 'components/Common/Cards/ActionCard';
import { useNavigate } from 'hooks/useNavigate';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { RoutePath } from 'enums/Routes';
import { ReactComponent as ProfileIcon } from 'images/profile-icon.svg';
import { ReactComponent as SettingsIcon } from 'images/settings-icon.svg';
import { ReactComponent as CashIcon } from 'images/cash-icon.svg';
import { infoNotification } from 'notifications';
import { showIntercom } from 'utils/intercom';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';

import { FLOW_TO_STEP_MAP, VERIFICATION_PENDING_STATUSES } from 'handlers/applicationStep';
import { CurrentFlow } from 'enums/CurrentFlow';

import styles from './YourPlanneryLoanSection.module.scss';

enum CardOption {
  ResumeApplication = 'ResumeApplication',
  ContactPlannery = 'ContactPlannery',
  ManageLoan = 'ManageLoan',
  MakePayment = 'MakePayment',
}

export type LoanActionCard<T> = {
  icon: React.ReactNode;
  label: string;
  action: T;
};

const HIDE_LOAN_SECTION_STATUSES = [ApplicationStatusName.AwaitingFunding, ApplicationStatusName.Rejected];

const YourPlanneryLoanSection: React.FC = () => {
  const { application } = useSelector(getApplicationData);
  const { breakdowns } = useSelector(getLoanBreakdown);
  const navigate = useNavigate();

  if (!application || HIDE_LOAN_SECTION_STATUSES.includes(application.status)) return null;

  const isLoanApproved = application.status === ApplicationStatusName.Funded;

  const isVerificationPending = VERIFICATION_PENDING_STATUSES.includes(application.status);

  const CARD_OPTION_ROUTES_MAP: Partial<Record<CardOption, RoutePath>> = {
    [CardOption.ResumeApplication]: FLOW_TO_STEP_MAP[CurrentFlow.DebtConsolidation](application).path,
    [CardOption.ManageLoan]: RoutePath.YourLoans,
  };

  const handleContinue = async (option: CardOption) => {
    // Handle Contact Plannery action - open Intercom
    if (option === CardOption.ContactPlannery) return showIntercom();

    // Handle Manage Loan action - navigate based on number of loans
    if (option === CardOption.ManageLoan) {
      if (breakdowns.length === 1) {
        const path = generatePath(RoutePath.ManageYourLoan, { loanId: breakdowns[0].id.toString() });
        return navigate(path as RoutePath);
      }

      return navigate(RoutePath.YourLoans);
    }

    const route = CARD_OPTION_ROUTES_MAP[option];

    if (route) {
      navigate(
        route as RoutePath,
        route === RoutePath.OfferAvailable ? OFFER_AVAILABLE_FROM_DASHBOARD_STATE : undefined,
      );
    } else {
      // Temporary placeholder for non-navigation actions
      infoNotification(`${option} - Coming soon!`);
    }
  };

  const contactPlanneryCard = {
    icon: <ProfileIcon className={styles.profileIcon} />,
    label: 'Contact Plannery',
    action: CardOption.ContactPlannery,
  };

  const cards: LoanActionCard<CardOption>[] = isLoanApproved
    ? [
        {
          icon: <SettingsIcon />,
          label: `Manage your loan${breakdowns.length > 1 ? 's' : ''}`,
          action: CardOption.ManageLoan,
        },
        // TODO: Add this card when we have the payment functionality
        // { icon: <MoneyIcon />, label: 'Make a loan payment', action: CardOption.MakePayment },
        contactPlanneryCard,
      ]
    : [
        {
          icon: <CashIcon />,
          label: isVerificationPending ? 'Resume application' : 'Offer status',
          action: CardOption.ResumeApplication,
        },
        contactPlanneryCard,
      ];

  const sectionTitle = `Your Plannery loan ${isLoanApproved ? '' : 'application'}`;

  return (
    <div className={styles.section}>
      <p className={styles.sectionLabel}>{sectionTitle}</p>
      <div className={styles.cardsContainer}>
        {cards.map((card, index) => (
          <ActionCard
            key={index}
            variant={ActionCardVariant.White}
            size={ActionCardSize.Compact}
            showChevron
            onClick={() => handleContinue(card.action)}
          >
            <ActionCard.Icon>{card.icon}</ActionCard.Icon>
            <ActionCard.Description>{card.label}</ActionCard.Description>
          </ActionCard>
        ))}
      </div>
    </div>
  );
};

export default YourPlanneryLoanSection;
